import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Star, ArrowRight, Send, Heart, Sparkles, Eye, AlertCircle, Users, MapPin, Stethoscope } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "sv_satisfaction_survey_draft";

// Enlaces a las fichas de Google (reseñas reales de Salud y Visión)
const GOOGLE_REVIEW_LINKS = {
  Madrid: "https://g.page/r/CcVrYiiV0TeaEBM/review",
  Alcobendas: "https://g.page/r/CYVbTdMwQP7FEBM/review",
};

// ==== Componente para escala 1-5 con estrellas ====
const StarScale = ({ value, onChange, name }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-2 mt-2" data-testid={`stars-${name}`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= (hover || value || 0);
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110 cursor-pointer"
            aria-label={`${n} estrella${n > 1 ? "s" : ""}`}
            data-testid={`star-${name}-${n}`}
          >
            <Star
              size={32}
              strokeWidth={1.5}
              style={{
                color: filled ? "#d4a017" : "var(--sage-200)",
                fill: filled ? "#f5c542" : "transparent",
              }}
            />
          </button>
        );
      })}
      {value > 0 && (
        <span className="ml-3 self-center font-sans-body text-sm" style={{ color: "var(--sage-600)" }}>
          {["", "Nada satisfecho", "Poco satisfecho", "Satisfecho", "Muy satisfecho", "Excelente"][value]}
        </span>
      )}
    </div>
  );
};

// ==== Componente escala 1-10 ====
const NumberScale = ({ value, onChange, name }) => (
  <div className="flex flex-wrap gap-2 mt-2" data-testid={`scale10-${name}`}>
    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
      const selected = value === n;
      return (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="h-11 w-11 rounded-xl font-display text-lg transition-all"
          style={{
            background: selected ? "var(--sage-500)" : "var(--cream)",
            color: selected ? "var(--cream)" : "var(--sage-600)",
            border: `1.5px solid ${selected ? "var(--sage-500)" : "var(--sage-200)"}`,
            transform: selected ? "scale(1.08)" : "scale(1)",
            boxShadow: selected ? "0 8px 20px -8px rgba(90,110,58,0.4)" : "none",
          }}
          data-testid={`scale10-${name}-${n}`}
        >
          {n}
        </button>
      );
    })}
  </div>
);

// ==== Componente radio simple ====
const RadioGroup = ({ options, value, onChange, name }) => (
  <div className="grid sm:grid-cols-2 gap-2 mt-2" data-testid={`radio-${name}`}>
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <label
          key={opt}
          className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all"
          style={{
            background: selected ? "var(--sage-50)" : "var(--cream)",
            border: `1.5px solid ${selected ? "var(--sage-500)" : "var(--sage-100)"}`,
          }}
        >
          <input
            type="radio"
            name={name}
            checked={selected}
            onChange={() => onChange(opt)}
            style={{ accentColor: "var(--sage-500)" }}
            data-testid={`radio-${name}-${opt.replace(/\s+/g, "_")}`}
          />
          <span className="font-sans-body text-sm" style={{ color: "var(--ink)" }}>{opt}</span>
        </label>
      );
    })}
  </div>
);

// ==== Checkbox múltiple ====
const CheckboxGroup = ({ options, value, onChange, name }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((v) => v !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-2 mt-2" data-testid={`checkbox-${name}`}>
      {options.map((opt) => {
        const isSel = selected.includes(opt);
        return (
          <label
            key={opt}
            className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all"
            style={{
              background: isSel ? "var(--sage-50)" : "var(--cream)",
              border: `1.5px solid ${isSel ? "var(--sage-500)" : "var(--sage-100)"}`,
            }}
          >
            <input
              type="checkbox"
              checked={isSel}
              onChange={() => toggle(opt)}
              style={{ accentColor: "var(--sage-500)" }}
              data-testid={`checkbox-${name}-${opt.replace(/\s+/g, "_")}`}
            />
            <span className="font-sans-body text-sm" style={{ color: "var(--ink)" }}>{opt}</span>
          </label>
        );
      })}
    </div>
  );
};

// ==== Página principal ====
const SatisfactionSurveyPage = () => {
  const [answers, setAnswers] = useState({});
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Auto-guardado en localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft && draft.answers) {
          setAnswers(draft.answers);
          setContactName(draft.contactName || "");
          setContactEmail(draft.contactEmail || "");
        }
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ answers, contactName, contactEmail }));
    } catch { /* noop */ }
  }, [answers, contactName, contactEmail]);

  const set = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

  // Total preguntas para progreso
  const REQUIRED_KEYS = [
    "centro", "tiempo",
    "atencion_equipo", "claridad_explicaciones", "seguimiento_familia", "instalaciones", "organizacion_puntualidad",
    "mejoras_notadas", "valoracion_terapeuta", "informacion_evolucion", "material_casa",
    "recomendaria", "valoracion_global",
  ];
  const answered = REQUIRED_KEYS.filter((k) => {
    const v = answers[k];
    return v !== undefined && v !== "" && v !== null;
  }).length;
  const progress = Math.round((answered / REQUIRED_KEYS.length) * 100);

  const submit = async (e) => {
    e.preventDefault();
    if (answered === 0) {
      toast.error("Por favor, responde al menos una pregunta antes de enviar.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/satisfaction-survey`, {
        answers,
        contact_name: contactName,
        contact_email: contactEmail,
        website: honeypot,
      });
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Ha habido un problema al enviar. Inténtalo en un momento.");
    } finally {
      setSubmitting(false);
    }
  };

  // ==== Pantalla de confirmación + reseñas Google ====
  if (done) {
    const centroElegido = answers.centro;
    const linkRecomendado = centroElegido && GOOGLE_REVIEW_LINKS[centroElegido];
    return (
      <main className="min-h-[70vh] flex items-center justify-center px-6 py-20" data-testid="survey-done">
        <div className="soft-card text-center max-w-2xl w-full">
          <div className="h-20 w-20 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--sage-100)" }}>
            <Heart size={42} style={{ color: "var(--sage-500)" }} />
          </div>
          <h1 className="section-title mt-6" style={{ fontSize: "2.2rem" }}>
            ¡Gracias de <span className="handwritten">corazón</span>!
          </h1>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-4 leading-relaxed">
            Vuestra opinión es lo que nos ayuda a seguir mejorando cada día. Os leeremos con mucha atención.
          </p>

          <div className="mt-10 p-6 rounded-2xl" style={{ background: "var(--sage-50)", border: "1px solid var(--sage-100)" }}>
            <Sparkles size={22} className="mx-auto" style={{ color: "var(--sage-500)" }} />
            <h2 className="font-display text-xl mt-3" style={{ color: "var(--ink)" }}>
              ¿Estáis contentos con vuestra experiencia?
            </h2>
            <p className="font-sans-body text-sm mt-2 text-[color:var(--ink-soft)]">
              Si os apetece, nos ayudaríais muchísimo a que más familias nos conozcan dejándonos una reseña en Google. ¡Solo lleva un momento!
            </p>
            <div className="mt-5 flex flex-wrap gap-3 justify-center">
              {linkRecomendado && (
                <a
                  href={linkRecomendado}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="google-review-recommended"
                >
                  <Star size={16} /> Dejar reseña · {centroElegido}
                </a>
              )}
              {!linkRecomendado && (
                <>
                  <a href={GOOGLE_REVIEW_LINKS.Madrid} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="google-review-madrid">
                    <Star size={16} /> Reseña · Madrid
                  </a>
                  <a href={GOOGLE_REVIEW_LINKS.Alcobendas} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="google-review-alcobendas">
                    <Star size={16} /> Reseña · Alcobendas
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Bloque de captación — para quien llega vía encuesta y aún no conoce toda la web */}
          <div className="mt-12 pt-10 border-t" style={{ borderColor: "var(--sage-100)" }}>
            <span className="section-eyebrow">Descubre más de Salud y Visión</span>
            <h2 className="font-display text-2xl mt-3" style={{ color: "var(--ink)" }}>
              Mira todo lo que <span className="handwritten">hacemos</span>
            </h2>
            <p className="font-sans-body text-sm mt-3 max-w-xl mx-auto leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              Quizá te sorprenda — hacemos mucho más que terapia visual. <strong>Si conoces a alguien que pueda necesitarnos, te agradeceríamos que nos compartieras.</strong>
            </p>

            <div className="mt-7 grid grid-cols-2 md:grid-cols-3 gap-3 text-left">
              {[
                { to: "/servicios", icon: Eye, label: "Nuestros servicios", desc: "Terapia visual, ambliopía, miopía, contactología..." },
                { to: "/sintomas", icon: AlertCircle, label: "Síntomas frecuentes", desc: "¿Conoces a alguien que se reconozca?" },
                { to: "/nosotros", icon: Users, label: "Conoce al equipo", desc: "Quiénes somos y cómo trabajamos" },
                { to: "/centros", icon: MapPin, label: "Nuestros centros", desc: "Madrid y nuevo en Alcobendas" },
                { to: "/profesionales", icon: Stethoscope, label: "Para profesionales", desc: "Fisios, psicólogos, logopedas..." },
                { to: "/", icon: ArrowRight, label: "Volver al inicio", desc: "Explora toda la web" },
              ].map((it) => (
                <Link
                  key={it.to + it.label}
                  to={it.to}
                  className="group p-4 rounded-2xl transition-all hover:scale-[1.02]"
                  style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }}
                  data-testid={`survey-explore-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-2" style={{ background: "var(--sage-100)" }}>
                    <it.icon size={18} style={{ color: "var(--sage-600)" }} />
                  </div>
                  <div className="font-sans-body font-semibold text-sm" style={{ color: "var(--ink)" }}>{it.label}</div>
                  <div className="font-sans-body text-xs mt-1 leading-snug" style={{ color: "var(--ink-soft)" }}>{it.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[70vh] py-12 px-6" data-testid="satisfaction-survey-page">
      {/* Honeypot anti-spam */}
      <input
        type="text"
        name="website"
        tabIndex="-1"
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
        aria-hidden="true"
      />

      <div className="max-w-3xl mx-auto">
        {/* HERO */}
        <div className="text-center fade-up">
          <div className="flex justify-center mb-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-sans-body text-xs" style={{ background: "var(--sage-100)", color: "var(--sage-700)" }}>
              <Sparkles size={12} /> Salud y Visión · Madrid y Alcobendas
            </div>
          </div>
          <span className="section-eyebrow">Tu opinión nos ayuda</span>
          <h1 className="section-title mt-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}>
            Encuesta de <span className="handwritten">satisfacción</span>
          </h1>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-4 max-w-xl mx-auto leading-relaxed">
            Tu opinión nos ayuda a seguir mejorando. Solo te llevará unos minutos. <strong>¡Gracias!</strong>
          </p>
        </div>

        {/* Progreso */}
        <div className="mt-10 mb-2 flex items-center justify-between font-sans-body text-xs" style={{ color: "var(--sage-600)" }}>
          <span>{answered} de {REQUIRED_KEYS.length} respondidas</span>
          <span>{progress}%</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--sage-100)" }}>
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: "var(--sage-500)" }} />
        </div>

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="survey-form">
          {/* === Sección 1 === */}
          <section className="soft-card">
            <h2 className="section-eyebrow">1 · Datos generales</h2>

            <div className="mt-5">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿En qué centro realizáis la terapia?</label>
              <RadioGroup
                name="centro"
                options={["Madrid", "Alcobendas"]}
                value={answers.centro}
                onChange={(v) => set("centro", v)}
              />
            </div>

            <div className="mt-6">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿Cuánto tiempo lleváis con nosotros?</label>
              <RadioGroup
                name="tiempo"
                options={["Menos de 3 meses", "Entre 3 y 6 meses", "Entre 6 meses y 1 año", "Más de 1 año"]}
                value={answers.tiempo}
                onChange={(v) => set("tiempo", v)}
              />
            </div>
          </section>

          {/* === Sección 2 — escalas con estrellas === */}
          <section className="soft-card">
            <h2 className="section-eyebrow">2 · Valoración del servicio</h2>
            <p className="font-sans-body text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
              Puntúa cada aspecto del 1 al 5 con las estrellas.
            </p>
            {[
              ["atencion_equipo", "Atención del equipo (trato, amabilidad, profesionalidad)"],
              ["claridad_explicaciones", "Claridad en las explicaciones sobre el tratamiento"],
              ["seguimiento_familia", "Seguimiento y comunicación con la familia"],
              ["instalaciones", "Instalaciones y comodidad del centro"],
              ["organizacion_puntualidad", "Organización y puntualidad de las sesiones"],
            ].map(([key, label]) => (
              <div key={key} className="mt-6">
                <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>{label}</label>
                <StarScale name={key} value={answers[key]} onChange={(v) => set(key, v)} />
              </div>
            ))}
          </section>

          {/* === Sección 3 === */}
          <section className="soft-card">
            <h2 className="section-eyebrow">3 · Resultados y evolución</h2>

            <div className="mt-5">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿Habéis notado mejoras desde el inicio del tratamiento?</label>
              <RadioGroup
                name="mejoras_notadas"
                options={["Sí, mejoras claras", "Sí, aunque aún queda camino", "Es pronto para valorarlo", "No hemos notado cambios"]}
                value={answers.mejoras_notadas}
                onChange={(v) => set("mejoras_notadas", v)}
              />
            </div>

            <div className="mt-6">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿En qué áreas habéis notado más cambios?</label>
              <p className="font-sans-body text-xs mt-1" style={{ color: "var(--ink-soft)" }}>Podéis marcar varias</p>
              <CheckboxGroup
                name="areas_cambio"
                options={["Lectura y escritura", "Rendimiento escolar", "Coordinación y motricidad", "Atención y concentración", "Cansancio visual", "Confianza y autoestima"]}
                value={answers.areas_cambio}
                onChange={(v) => set("areas_cambio", v)}
              />
            </div>

            <div className="mt-6">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿Cómo valoráis el esfuerzo y la dedicación del/la terapeuta?</label>
              <RadioGroup
                name="valoracion_terapeuta"
                options={["Excelente", "Muy buena", "Buena", "Mejorable"]}
                value={answers.valoracion_terapeuta}
                onChange={(v) => set("valoracion_terapeuta", v)}
              />
            </div>
          </section>

          {/* === Sección 4 === */}
          <section className="soft-card">
            <h2 className="section-eyebrow">4 · Comunicación y acompañamiento</h2>

            <div className="mt-5">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿Os habéis sentido bien informados/as sobre la evolución?</label>
              <RadioGroup
                name="informacion_evolucion"
                options={["Sí, siempre", "Generalmente sí", "A veces", "No suficientemente"]}
                value={answers.informacion_evolucion}
                onChange={(v) => set("informacion_evolucion", v)}
              />
            </div>

            <div className="mt-6">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿El material para trabajar en casa ha sido útil y comprensible?</label>
              <RadioGroup
                name="material_casa"
                options={["Sí, muy útil", "Útil, aunque complejo a veces", "Podría mejorarse", "No lo hemos podido usar bien"]}
                value={answers.material_casa}
                onChange={(v) => set("material_casa", v)}
              />
            </div>
          </section>

          {/* === Sección 5 === */}
          <section className="soft-card">
            <h2 className="section-eyebrow">5 · Valoración global</h2>

            <div className="mt-5">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿Recomendaríais Salud y Visión a otras familias?</label>
              <RadioGroup
                name="recomendaria"
                options={["Sí, sin duda", "Probablemente sí", "No estoy seguro/a", "Probablemente no"]}
                value={answers.recomendaria}
                onChange={(v) => set("recomendaria", v)}
              />
            </div>

            <div className="mt-6">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>Valoración general del centro (del 1 al 10)</label>
              <NumberScale name="valoracion_global" value={answers.valoracion_global} onChange={(v) => set("valoracion_global", v)} />
            </div>
          </section>

          {/* === Sección 6 — texto libre === */}
          <section className="soft-card">
            <h2 className="section-eyebrow">6 · Tu opinión, en tus palabras</h2>

            <div className="mt-5">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿Qué es lo que más valoráis de vuestra experiencia?</label>
              <textarea
                rows="4"
                className="field-textarea mt-2"
                value={answers.mas_valorado || ""}
                onChange={(e) => set("mas_valorado", e.target.value)}
                placeholder="Cuéntanos lo que más te gusta..."
                data-testid="textarea-mas_valorado"
              />
            </div>

            <div className="mt-6">
              <label className="font-sans-body font-medium" style={{ color: "var(--ink)" }}>¿Hay algo que cambiaríais o mejorarías?</label>
              <textarea
                rows="4"
                className="field-textarea mt-2"
                value={answers.mejoras_sugeridas || ""}
                onChange={(e) => set("mejoras_sugeridas", e.target.value)}
                placeholder="Toda sugerencia es bienvenida..."
                data-testid="textarea-mejoras_sugeridas"
              />
            </div>
          </section>

          {/* === Identificación opcional === */}
          <section className="soft-card" style={{ background: "var(--bg-soft)" }}>
            <h2 className="section-eyebrow">¿Quieres que te respondamos? <span style={{ textTransform: "none", letterSpacing: 0, fontSize: "11px" }}>(opcional)</span></h2>
            <p className="font-sans-body text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
              Si nos dejas tu nombre y email, podremos responderte si lo necesitas. La encuesta es totalmente anónima si lo prefieres.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="field-label">Nombre</label>
                <input
                  type="text"
                  className="field-input"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  data-testid="survey-contact-name"
                />
              </div>
              <div>
                <label className="field-label">Email</label>
                <input
                  type="email"
                  className="field-input"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  data-testid="survey-contact-email"
                />
              </div>
            </div>
          </section>

          {/* === Enviar === */}
          <div className="flex flex-col items-center gap-3 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary text-base px-8 py-4"
              data-testid="survey-submit"
            >
              {submitting ? "Enviando..." : <>Enviar encuesta <Send size={16} /></>}
            </button>
            <p className="font-sans-body text-xs" style={{ color: "var(--ink-soft)" }}>
              <CheckCircle2 size={12} className="inline mr-1" style={{ color: "var(--sage-500)" }} />
              Se guarda automáticamente · Puedes volver más tarde
            </p>
          </div>
        </form>
      </div>
    </main>
  );
};

export default SatisfactionSurveyPage;
